/**
=========================================================
* Trust Cargo - one supplier's payment history
=========================================================

* Opens over whatever list you were reading and shows every payment made to
* that supplier, with their account totals above it - so "how much have we
* actually sent AAJ" is one click from any row, without losing your place.
*
* Reads /v2/suppliers/{id}/statement, which returns the supplier, the
* invoiced / paid / balance totals and both ledgers. Only the payments are
* shown here; the purchases side lives on the Customer Suppliers report.
*/

import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";

const API = "https://trust-cargo.co/anas_backend/api/v2";

const money = (v) =>
  v === null || v === undefined || v === ""
    ? "-"
    : Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** One total, boxed. */
function Total({ label, value, tone }) {
  return (
    <SuiBox
      px={2}
      py={1.25}
      sx={({ palette, borders, functions: { rgba } }) => ({
        flex: "1 1 140px",
        borderRadius: borders.borderRadius.md,
        border: `1px solid ${rgba(palette.dark.main, 0.1)}`,
        backgroundColor: rgba(palette[tone].main, 0.06),
      })}
    >
      <SuiTypography variant="caption" sx={{ opacity: 0.7, display: "block" }}>
        {label}
      </SuiTypography>
      <SuiTypography
        variant="h6"
        sx={({ palette }) => ({ color: palette[tone].main, fontWeight: 700 })}
      >
        {value}
      </SuiTypography>
    </SuiBox>
  );
}

Total.defaultProps = { tone: "dark" };
Total.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  tone: PropTypes.string,
};

function SupplierPaymentsDialog({ open, supplierId, supplierName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !supplierId) return;

    setLoading(true);
    setError("");
    setData(null);

    fetch(`${API}/suppliers/${supplierId}/statement`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setData)
      .catch((e) => setError(e?.message || "Could not load the payment history"))
      .finally(() => setLoading(false));
  }, [open, supplierId]);

  const payments = data?.payments || [];
  const shown = payments.reduce((s, p) => s + (Number(p.amount_base ?? p.amount) || 0), 0);

  const cell = { fontSize: 14, whiteSpace: "nowrap" };

  const heading = data?.supplier
    ? [data.supplier.code, data.supplier.name].filter(Boolean).join(" - ")
    : supplierName || "Supplier";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: ({ borders, boxShadows }) => ({
          borderRadius: borders.borderRadius.lg,
          boxShadow: boxShadows.xl,
        }),
      }}
    >
      <DialogTitle
        sx={({ palette, functions: { rgba } }) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${rgba(palette.dark.main, 0.08)}`,
          pb: 1.5,
        })}
      >
        <SuiBox>
          <SuiTypography variant="h5" fontWeight="bold">
            Payment history
          </SuiTypography>
          <SuiTypography variant="caption" sx={{ opacity: 0.7 }}>
            {heading}
          </SuiTypography>
        </SuiBox>

        <Icon
          onClick={onClose}
          sx={({ palette, functions: { rgba } }) => ({
            cursor: "pointer",
            fontSize: 26,
            color: rgba(palette.dark.main, 0.45),
            "&:hover": { color: palette.error.main },
          })}
        >
          close
        </Icon>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {loading && <CargoLoader label="Loading payments" size={70} />}

        {error && !loading && <Alert severity="error">{error}</Alert>}

        {data && !loading && (
          <>
            <SuiBox display="flex" gap={1.5} flexWrap="wrap" mb={3}>
              <Total label="Invoiced" value={money(data.totals?.invoiced)} />
              <Total label="Paid" value={money(data.totals?.paid)} tone="success" />
              <Total
                label="Balance"
                value={money(data.totals?.balance)}
                tone={Number(data.totals?.balance) > 0 ? "error" : "success"}
              />
            </SuiBox>

            <SuiBox sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 620 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Payment date</TableCell>
                    <TableCell align="center">Method</TableCell>
                    <TableCell align="center">Reference</TableCell>
                    <TableCell align="center">Amount</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {payments.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell align="center" sx={cell}>
                        {p.paid_at || "-"}
                      </TableCell>
                      <TableCell align="center" sx={cell}>
                        {p.method || "-"}
                      </TableCell>
                      <TableCell align="center" sx={cell}>
                        {p.reference || "-"}
                      </TableCell>
                      <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                        {`${money(p.amount)} ${p.currency || ""}`}
                      </TableCell>
                    </TableRow>
                  ))}

                  {payments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <SuiTypography variant="button" color="text">
                          Nothing has been paid to this supplier yet
                        </SuiTypography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </SuiBox>

            {payments.length > 0 && (
              <SuiBox
                mt={2}
                pt={2}
                display="flex"
                justifyContent="space-between"
                sx={({ palette, functions: { rgba } }) => ({
                  borderTop: `1px solid ${rgba(palette.dark.main, 0.08)}`,
                })}
              >
                <SuiTypography variant="button" sx={{ opacity: 0.7 }}>
                  {`${payments.length} payment${payments.length === 1 ? "" : "s"}`}
                </SuiTypography>
                <SuiTypography variant="button" fontWeight="bold">
                  {`Total ${money(shown)}`}
                </SuiTypography>
              </SuiBox>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button onClick={onClose} size="large" variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SupplierPaymentsDialog.defaultProps = { supplierId: null, supplierName: "" };

SupplierPaymentsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  supplierId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // shown while the statement is still loading
  supplierName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default SupplierPaymentsDialog;
